import React, {useMemo} from 'react'
import {IntlShape, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {useSeoBreadcrumb} from 'hooks/useSeoBreadcrumb'
import {useSeoSRPMetadata} from 'hooks/useSeoSRPMetadata'
import {getCurrencyCode, getLocaleCode} from 'modules/meta/selectors'
import {
  getAnchor,
  getHotelEntities,
  getHotelIdsToDisplay
} from 'modules/sapiSearch/selectors'
import {getListOfValues} from 'modules/search/selectors'
import Head from 'next/head'
import {getRankedAvailableFacilities} from 'utils/facilities'
import {
  getBreadcrumbSchema,
  getOrganizationSchema,
  getSchemaFromHotel,
  getWebsiteSchema
} from 'utils/seo/getJsonLdSchemas'
import {generateAccommodationPageURL} from 'utils/url'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {formatPriceValue} from '@vio/core/src/price/business/formatPrice'
import {getRoundingStrategy} from '@vio/core/src/utils/number'

import type {Hotel, PlaceAnchor} from '@findhotel/sapi'
import type {Lov} from 'modules/search/slice'
import type {SeoMetadata} from 'utils/server/searchSeo/getSeoMetadata'

export const getCitySchema = ({
  baseUrl,
  cityPath,
  anchor
}: {
  baseUrl: string
  cityPath: string
  anchor: PlaceAnchor
}) => {
  if (!cityPath || !anchor) return null

  const {_geoloc: geolocation, navPathInfo = {}} = anchor
  const {city, country, adminDivision} = navPathInfo

  return {
    '@type': 'City',
    '@id': `${baseUrl}${cityPath}#city`,
    name: city?.name,
    address: {
      '@type': 'PostalAddress',
      addressLocality: city?.name,
      addressRegion: adminDivision?.name,
      addressCountry: country?.name
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: geolocation.lat,
      longitude: geolocation.lon
    }
  }
}

export const getSearchResultsPageSchema = ({
  baseUrl,
  seoSRPUrl,
  cityPath,
  metadata,
  locale
}: {
  baseUrl: string
  seoSRPUrl: string
  cityPath: string
  metadata: SeoMetadata
  locale: string
}) => {
  if (!metadata) return null

  return {
    '@type': 'SearchResultsPage',
    '@id': `${seoSRPUrl}`,
    url: `${seoSRPUrl}`,
    name: metadata.heading,
    inLanguage: locale,
    breadcrumb: {
      '@id': `${seoSRPUrl}#breadcrumb`
    },
    about: {
      '@id': `${seoSRPUrl}`
    },
    mainEntity: {
      '@id': `${seoSRPUrl}#hotel-results`
    },
    sourceOrganization: {
      '@id': `${baseUrl}/#organization`
    },
    ...(cityPath && {
      contentLocation: {
        '@id': `${baseUrl}${cityPath}#city`
      }
    })
  }
}

export const getItemListSchema = ({
  locale,
  currencyCode,
  baseUrl,
  hotelIds,
  hotelEntities,
  lov,
  seoSRPUrl,
  metadata,
  formatMessage
}: {
  locale: string
  currencyCode: string
  baseUrl: string
  hotelIds: string[]
  hotelEntities: Record<string, Hotel>
  lov: Lov
  seoSRPUrl: string
  metadata: SeoMetadata
  formatMessage: IntlShape['formatMessage']
}) => {
  if (!metadata) return null

  const formatPrice = (price: number) =>
    formatPriceValue({
      locale,
      roundingStrategy: getRoundingStrategy(),
      value: price,
      currency: currencyCode,
      maximumFractionDigits: 0
    })

  const itemListElement = hotelIds
    .map((hotelId, index) => {
      const hotel = hotelEntities[hotelId] as Hotel

      if (!hotel) return null

      const hotelURL = baseUrl + generateAccommodationPageURL(hotelId, locale)

      const facilities = getRankedAvailableFacilities(hotel.facilities, lov)

      const amenityFeature = facilities.map(facility => ({
        '@type': 'LocationFeatureSpecification',
        name: facility.value,
        value: true
      }))

      const priceRange = hotel.regularPriceRange
        ? formatMessage(
            {
              id: 'priceRangePerNight',
              defaultMessage: 'From {minPrice} to {maxPrice} per night'
            },
            {
              minPrice: formatPrice(hotel.regularPriceRange[0]),
              maxPrice: formatPrice(hotel.regularPriceRange[1])
            }
          )
        : undefined

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'LodgingBusiness',
          '@id': hotelURL,
          name: hotel.hotelName,
          url: hotelURL,
          ...getSchemaFromHotel('Image', hotel),
          ...(priceRange && {priceRange}),
          ...getSchemaFromHotel('PostalAddress', hotel),
          ...getSchemaFromHotel('AggregateRating', hotel),
          ...getSchemaFromHotel('Rating', hotel),
          amenityFeature
        }
      }
    })
    .filter(Boolean)

  return {
    '@type': 'ItemList',
    '@id': `${seoSRPUrl}#hotel-results`,
    name: metadata.heading,
    numberOfItems: hotelIds.length,
    itemListElement
  }
}

export const SeoStructuredData: React.FC = () => {
  const {brand} = useBrand()
  const locale = useSelector(getLocaleCode)
  const currencyCode = useSelector(getCurrencyCode)
  const anchor = useSelector(getAnchor) as PlaceAnchor
  const breadcrumb = useSeoBreadcrumb()
  const metadata = useSeoSRPMetadata()
  const hotelIds = useSelector(getHotelIdsToDisplay)
  const hotelEntities = useSelector(getHotelEntities)
  const lov = useSelector(getListOfValues)
  const {formatMessage} = useIntl()

  const schemas = useMemo(() => {
    if (!metadata) return null

    const baseUrl = brand.siteAddresses.address
    const seoSRPPath = breadcrumb.at(-1)?.path

    if (!seoSRPPath) return null

    const seoSRPUrl = `${baseUrl}${seoSRPPath}`
    const cityPath = breadcrumb.find(item => item.type === 'city')?.path

    return [
      getOrganizationSchema({brand}),
      getWebsiteSchema({brand}),
      getCitySchema({baseUrl, cityPath, anchor}),
      getBreadcrumbSchema({baseUrl, breadcrumb, seoPageUrl: seoSRPUrl}),
      getSearchResultsPageSchema({
        baseUrl,
        seoSRPUrl,
        metadata,
        locale,
        cityPath
      }),
      getItemListSchema({
        locale,
        currencyCode,
        baseUrl,
        hotelIds,
        hotelEntities,
        lov,
        seoSRPUrl,
        metadata,
        formatMessage
      })
    ].filter(Boolean)
  }, [
    currencyCode,
    formatMessage,
    brand,
    locale,
    anchor,
    breadcrumb,
    hotelIds,
    hotelEntities,
    lov,
    metadata
  ])

  if (!metadata) return null

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': schemas
          })
        }}
      />
    </Head>
  )
}
